<template>
    <div class="min-h-screen bg-white">
        <div v-if="pageLoading" class="flex items-center justify-center min-h-[60vh]">
            <Loader size="large" />
        </div>
        <div v-else class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <div class="text-center mb-2.5">
                <h1 class="text-4xl md:text-6xl font-marketing font-bold text-primary mb-1.5">
                    Donate
                </h1>
                <div v-if="pageData?.description"
                    class="text-xl text-gray-700 max-w-4xl mx-auto font-sans leading-relaxed content-page-description"
                    v-html="pageData.description">
                </div>
            </div>

            <div v-if="pageData?.sections && pageData.sections.length > 0"
                class="max-w-4xl mx-auto space-y-[0.196rem] mb-0">
                <div v-for="section in pageData.sections" :key="section.title"
                    class="bg-white rounded-lg shadow-sm p-2.5 md:p-4">
                    <h2 v-if="section.title" class="text-3xl md:text-4xl font-marketing font-bold text-primary mb-1">
                        {{ section.title }}
                    </h2>
                    <div class="text-lg text-gray-700 font-sans leading-relaxed content-page-section"
                        v-html="section.content"></div>
                </div>
            </div>

            <div class="bg-white rounded-lg shadow-sm p-2.5 md:p-4 max-w-2xl mx-auto">
                <div v-if="!showPaymentMethod && !showPayment" class="space-y-1.5">
                    <form @submit.prevent="handleDonate" class="space-y-1.5">
                        <div>
                            <label for="amount" class="block text-sm font-marketing font-semibold text-gray-700 mb-1">
                                Donation Amount ($) <span class="text-red-500">*</span>
                            </label>
                            <input id="amount" v-model.number="donationForm.amount" type="number" step="0.01" min="0.01"
                                required
                                class="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent font-sans"
                                placeholder="0.00" />
                        </div>

                        <div>
                            <label for="name" class="block text-sm font-marketing font-semibold text-gray-700 mb-2.5">
                                Name (Optional)
                            </label>
                            <input id="name" v-model="donationForm.name" type="text"
                                class="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent font-sans"
                                placeholder="Your name" />
                        </div>

                        <div>
                            <label for="email" class="block text-sm font-marketing font-semibold text-gray-700 mb-2.5">
                                Email (Optional)
                            </label>
                            <input id="email" v-model="donationForm.email" type="email"
                                class="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent font-sans"
                                placeholder="your.email@example.com" />
                        </div>

                        <div>
                            <label for="note" class="block text-sm font-marketing font-semibold text-gray-700 mb-2">
                                Personal Note (Optional)
                            </label>
                            <textarea id="note" v-model="donationForm.note" rows="4"
                                class="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent font-sans"
                                placeholder="Leave a message..."></textarea>
                        </div>

                        <div v-if="error" class="p-3 bg-red-50 border border-red-200 rounded-md">
                            <p class="text-sm text-red-800 font-sans">{{ error }}</p>
                        </div>

                        <button type="submit" :disabled="processing || !donationForm.amount || donationForm.amount <= 0"
                            class="w-full bg-accent text-white py-3 px-8 rounded-md font-marketing font-semibold hover:bg-green-700 transition-colors text-lg disabled:opacity-50 disabled:cursor-not-allowed">
                            {{ processing ? 'Processing...' : `Donate $${donationForm.amount ?
                                donationForm.amount.toFixed(2) : '0.00'}` }}
                        </button>
                    </form>
                </div>

                <div v-if="showPaymentMethod && !showPayment && !paypalCheckoutUrl && !stripeCheckoutUrl"
                    class="bg-white border border-gray-200 rounded-lg p-1.5 mb-1">
                    <h2 class="text-2xl font-marketing font-bold text-primary mb-1">Select Payment Method</h2>

                    <div v-if="error" class="mb-1 p-1 bg-red-50 border border-red-200 rounded-md">
                        <div class="flex items-start">
                            <svg class="w-5 h-5 text-red-600 mt-0.5 mr-2 flex-shrink-0" fill="currentColor"
                                viewBox="0 0 20 20">
                                <path fill-rule="evenodd"
                                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                                    clip-rule="evenodd" />
                            </svg>
                            <div class="flex-1">
                                <p class="text-sm text-red-800 font-sans font-semibold mb-1">Error</p>
                                <p class="text-sm text-red-700 font-sans">{{ error }}</p>
                            </div>
                            <button @click="error = null" class="text-red-400 hover:text-red-600 ml-2">
                                <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                    <path fill-rule="evenodd"
                                        d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                        clip-rule="evenodd" />
                                </svg>
                            </button>
                        </div>
                    </div>

                    <div class="space-y-0.5">
                        <button @click.stop="selectPaymentMethod('stripe')"
                            class="w-full p-4 border-2 rounded-md transition-colors text-left overflow-hidden"
                            :class="selectedPaymentMethod === 'stripe' ? 'border-green-700 bg-green-50' : 'border-gray-300 hover:border-green-500'">
                            <div class="flex items-center gap-3 min-w-0">
                                <div class="flex items-center flex-1 min-w-0">
                                    <input type="radio" :checked="selectedPaymentMethod === 'stripe'"
                                        class="mr-3 flex-shrink-0" />
                                    <div class="min-w-0">
                                        <div class="font-marketing font-semibold text-primary">Credit/Debit Card</div>
                                        <div class="text-sm text-gray-600 font-sans">Pay with Visa, Mastercard, or other
                                            cards</div>
                                    </div>
                                </div>
                                <div class="flex items-center space-x-2 flex-shrink-0">
                                    <svg class="w-10 h-6" viewBox="0 0 24 24" fill="none"
                                        xmlns="http://www.w3.org/2000/svg">
                                        <path :d="siVisa.path" :fill="`#${siVisa.hex}`" />
                                    </svg>
                                    <svg class="w-10 h-6" viewBox="0 0 24 24" fill="none"
                                        xmlns="http://www.w3.org/2000/svg">
                                        <path :d="siMastercard.path" :fill="`#${siMastercard.hex}`" />
                                    </svg>
                                    <svg class="w-10 h-6" viewBox="0 0 24 24" fill="none"
                                        xmlns="http://www.w3.org/2000/svg">
                                        <path :d="siAmericanexpress.path" :fill="`#${siAmericanexpress.hex}`" />
                                    </svg>
                                    <svg class="w-10 h-6" viewBox="0 0 24 24" fill="none"
                                        xmlns="http://www.w3.org/2000/svg">
                                        <path :d="siDiscover.path" :fill="`#${siDiscover.hex}`" />
                                    </svg>
                                </div>
                            </div>
                        </button>

                        <button @click.stop="selectPaymentMethod('paypal')"
                            class="w-full p-4 border-2 rounded-md transition-colors text-left overflow-hidden"
                            :class="selectedPaymentMethod === 'paypal' ? 'border-green-700 bg-green-50' : 'border-gray-300 hover:border-green-500'">
                            <div class="flex items-center gap-3 min-w-0">
                                <div class="flex items-center flex-1 min-w-0">
                                    <input type="radio" :checked="selectedPaymentMethod === 'paypal'"
                                        class="mr-3 flex-shrink-0" />
                                    <div class="min-w-0">
                                        <div class="font-marketing font-semibold text-primary">PayPal</div>
                                        <div class="text-sm text-gray-600 font-sans">Pay securely with your PayPal
                                            account</div>
                                    </div>
                                </div>
                                <div class="flex items-center flex-shrink-0">
                                    <img src="/logos/paypal.png" alt="PayPal" class="h-8 w-auto" />
                                </div>
                            </div>
                        </button>
                    </div>

                    <div class="flex justify-between items-center mt-1 text-lg font-marketing font-bold text-primary">
                        <span>Total</span>
                        <span>${{ donationForm.amount.toFixed(2) }}</span>
                    </div>

                    <div class="flex gap-4 mt-0.5">
                        <button @click="showPaymentMethod = false; selectedPaymentMethod = null"
                            class="flex-1 bg-gray-300 text-gray-700 px-6 py-3 rounded-md hover:bg-gray-400 transition-colors font-marketing font-semibold">
                            Back
                        </button>
                        <button @click.stop="proceedWithPaymentMethod" :disabled="!selectedPaymentMethod || processing"
                            class="flex-1 bg-accent text-white px-6 py-3 rounded-md hover:bg-green-600 transition-colors font-marketing font-semibold disabled:opacity-50 disabled:cursor-not-allowed">
                            {{ processing ? 'Processing...' : 'Continue' }}
                        </button>
                    </div>
                </div>

                <div v-if="paypalCheckoutUrl && selectedPaymentMethod === 'paypal'"
                    class="bg-white border border-gray-200 rounded-lg p-6 mb-6">
                    <div class="text-center py-4">
                        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-accent mx-auto mb-4"></div>
                        <p class="text-gray-600 font-sans mb-2">Redirecting to PayPal...</p>
                        <p class="text-sm text-gray-500 font-sans mb-4">If you are not redirected automatically, <a
                                :href="paypalCheckoutUrl" target="_blank"
                                class="text-blue-600 underline hover:text-blue-700">click here</a></p>
                        <button @click="cancelPayPalRedirect"
                            class="mt-4 text-sm text-gray-600 hover:text-gray-800 underline font-sans">
                            Cancel and choose different payment method
                        </button>
                    </div>
                </div>

                <div v-if="stripeCheckoutUrl && selectedPaymentMethod === 'stripe'"
                    class="bg-white border border-gray-200 rounded-lg p-6 mb-6">
                    <div class="text-center py-4">
                        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-accent mx-auto mb-4"></div>
                        <p class="text-gray-600 font-sans mb-2">Redirecting to Stripe...</p>
                        <p class="text-sm text-gray-500 font-sans mb-4">If you are not redirected automatically, <a
                                :href="stripeCheckoutUrl" target="_blank"
                                class="text-blue-600 underline hover:text-blue-700">click here</a></p>
                        <button @click="cancelStripeRedirect"
                            class="mt-4 text-sm text-gray-600 hover:text-gray-800 underline font-sans">
                            Cancel and choose different payment method
                        </button>
                    </div>
                </div>

                <div v-if="showPaymentMethod && !showPayment && paypalCheckoutUrl && selectedPaymentMethod !== 'paypal' && !stripeCheckoutUrl"
                    class="bg-white border border-gray-200 rounded-lg p-6 mb-6">
                    <h2 class="text-2xl font-marketing font-bold text-primary mb-6">Select Payment Method</h2>

                    <div v-if="error" class="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
                        <div class="flex items-start">
                            <svg class="w-5 h-5 text-red-600 mt-0.5 mr-2 flex-shrink-0" fill="currentColor"
                                viewBox="0 0 20 20">
                                <path fill-rule="evenodd"
                                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                                    clip-rule="evenodd" />
                            </svg>
                            <div class="flex-1">
                                <p class="text-sm text-red-800 font-sans font-semibold mb-1">Error</p>
                                <p class="text-sm text-red-700 font-sans">{{ error }}</p>
                            </div>
                            <button @click="error = null" class="text-red-400 hover:text-red-600 ml-2">
                                <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                    <path fill-rule="evenodd"
                                        d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                        clip-rule="evenodd" />
                                </svg>
                            </button>
                        </div>
                    </div>

                    <div class="space-y-0.5">
                        <button @click.stop="selectPaymentMethod('stripe')"
                            class="w-full p-4 border-2 rounded-md transition-colors text-left overflow-hidden"
                            :class="selectedPaymentMethod === 'stripe' ? 'border-green-700 bg-green-50' : 'border-gray-300 hover:border-green-500'">
                            <div class="flex items-center gap-3 min-w-0">
                                <div class="flex items-center flex-1 min-w-0">
                                    <input type="radio" :checked="selectedPaymentMethod === 'stripe'"
                                        class="mr-3 flex-shrink-0" />
                                    <div class="min-w-0">
                                        <div class="font-marketing font-semibold text-primary">Credit/Debit Card</div>
                                        <div class="text-sm text-gray-600 font-sans">Pay with Visa, Mastercard, or other
                                            cards</div>
                                    </div>
                                </div>
                                <div class="flex items-center space-x-2 flex-shrink-0">
                                    <svg class="w-10 h-6" viewBox="0 0 24 24" fill="none"
                                        xmlns="http://www.w3.org/2000/svg">
                                        <path :d="siVisa.path" :fill="`#${siVisa.hex}`" />
                                    </svg>
                                    <svg class="w-10 h-6" viewBox="0 0 24 24" fill="none"
                                        xmlns="http://www.w3.org/2000/svg">
                                        <path :d="siMastercard.path" :fill="`#${siMastercard.hex}`" />
                                    </svg>
                                    <svg class="w-10 h-6" viewBox="0 0 24 24" fill="none"
                                        xmlns="http://www.w3.org/2000/svg">
                                        <path :d="siAmericanexpress.path" :fill="`#${siAmericanexpress.hex}`" />
                                    </svg>
                                    <svg class="w-10 h-6" viewBox="0 0 24 24" fill="none"
                                        xmlns="http://www.w3.org/2000/svg">
                                        <path :d="siDiscover.path" :fill="`#${siDiscover.hex}`" />
                                    </svg>
                                </div>
                            </div>
                        </button>

                        <button @click.stop="selectPaymentMethod('paypal')"
                            class="w-full p-4 border-2 rounded-md transition-colors text-left overflow-hidden"
                            :class="selectedPaymentMethod === 'paypal' ? 'border-green-700 bg-green-50' : 'border-gray-300 hover:border-green-500'">
                            <div class="flex items-center gap-3 min-w-0">
                                <div class="flex items-center flex-1 min-w-0">
                                    <input type="radio" :checked="selectedPaymentMethod === 'paypal'"
                                        class="mr-3 flex-shrink-0" />
                                    <div class="min-w-0">
                                        <div class="font-marketing font-semibold text-primary">PayPal</div>
                                        <div class="text-sm text-gray-600 font-sans">Pay securely with your PayPal
                                            account</div>
                                    </div>
                                </div>
                                <div class="flex items-center flex-shrink-0">
                                    <img src="/logos/paypal.png" alt="PayPal" class="h-8 w-auto" />
                                </div>
                            </div>
                        </button>
                    </div>

                    <div class="flex justify-between items-center mt-1 text-lg font-marketing font-bold text-primary">
                        <span>Total</span>
                        <span>${{ donationForm.amount.toFixed(2) }}</span>
                    </div>

                    <div class="flex gap-4 mt-0.5">
                        <button @click="showPaymentMethod = false; selectedPaymentMethod = null; cancelPayPalRedirect()"
                            class="flex-1 bg-gray-300 text-gray-700 px-6 py-3 rounded-md hover:bg-gray-400 transition-colors font-marketing font-semibold">
                            Back
                        </button>
                        <button @click.stop="proceedWithPaymentMethod" :disabled="!selectedPaymentMethod || processing"
                            class="flex-1 bg-accent text-white px-6 py-3 rounded-md hover:bg-green-600 transition-colors font-marketing font-semibold disabled:opacity-50 disabled:cursor-not-allowed">
                            {{ processing ? 'Processing...' : 'Continue' }}
                        </button>
                    </div>
                </div>

                <div v-else-if="showPayment" class="bg-white border border-gray-200 rounded-lg p-6 mb-6">
                    <div class="flex items-center justify-between mb-6">
                        <h2 class="text-2xl font-marketing font-bold text-primary">Payment</h2>
                        <button @click="cancelPayment"
                            class="text-sm text-gray-600 hover:text-gray-800 underline font-sans">
                            Cancel and choose different payment method
                        </button>
                    </div>

                    <div v-if="error" class="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
                        <div class="flex items-start">
                            <svg class="w-5 h-5 text-red-600 mt-0.5 mr-2 flex-shrink-0" fill="currentColor"
                                viewBox="0 0 20 20">
                                <path fill-rule="evenodd"
                                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                                    clip-rule="evenodd" />
                            </svg>
                            <div class="flex-1">
                                <p class="text-sm text-red-800 font-sans font-semibold mb-1">Error</p>
                                <p class="text-sm text-red-700 font-sans">{{ error }}</p>
                            </div>
                            <button @click="error = null" class="text-red-400 hover:text-red-600 ml-2">
                                <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                    <path fill-rule="evenodd"
                                        d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                        clip-rule="evenodd" />
                                </svg>
                            </button>
                        </div>
                    </div>

                    <div v-if="!paymentIntentId" class="text-center py-4">
                        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-accent mx-auto mb-2"></div>
                        <p class="text-gray-600 font-sans">Processing payment...</p>
                    </div>

                    <div v-else>
                        <div id="payment-request-button" class="mb-4"></div>
                        <div v-if="paymentRequestButton && cardElement" class="relative mb-4">
                            <div class="absolute inset-0 flex items-center">
                                <div class="w-full border-t border-gray-300"></div>
                            </div>
                            <div class="relative flex justify-center text-sm">
                                <span class="px-2 bg-white text-gray-500 font-sans">Or pay with card</span>
                            </div>
                        </div>
                        <div id="card-element" class="mb-6 p-3 border border-gray-300 rounded-md bg-white"></div>

                        <div
                            class="flex justify-between items-center mb-4 text-lg font-marketing font-bold text-primary">
                            <span>Total</span>
                            <span>${{ donationForm.amount.toFixed(2) }}</span>
                        </div>

                        <button @click="handlePayment" :disabled="processing"
                            class="w-full bg-accent text-white px-6 py-3 rounded-md hover:bg-green-600 transition-colors font-marketing font-semibold disabled:opacity-50 disabled:cursor-not-allowed">
                            {{ processing ? 'Processing...' : `Pay $${donationForm.amount.toFixed(2)}` }}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, watch, onMounted, onUnmounted } from 'vue'
import { loadStripe } from '@stripe/stripe-js'
import { STRIPE_PUBLISHABLE_KEY } from '../config'
import { donationService } from '../services/donation'
import { publicService } from '../services/public'
import Loader from './Loader.vue'
import { siVisa, siMastercard, siAmericanexpress, siDiscover } from 'simple-icons'

const donationForm = ref({
    amount: null,
    name: '',
    email: '',
    note: ''
})

const showPaymentMethod = ref(false)
const showPayment = ref(false)
const selectedPaymentMethod = ref(null)
const paypalCheckoutUrl = ref(null)
const stripeCheckoutUrl = ref(null)
const stripe = ref(null)
const elements = ref(null)
const cardElement = ref(null)
const paymentRequestButton = ref(null)
const paymentIntentId = ref(null)
const clientSecret = ref(null)
const processing = ref(false)
const error = ref(null)
const pageData = ref(null)
const pageLoading = ref(true)

const loadPage = async () => {
    pageLoading.value = true
    try {
        const data = await publicService.getPage('Donate')
        pageData.value = data
    } catch (err) {
        console.error('Failed to load Donate page:', err)
        pageData.value = {
            title: 'Donate',
            description: 'We are grateful for your support. Every contribution helps us continue our mission of bringing exceptional literature to readers around the world and supporting the talented authors who make it all possible.',
            sections: []
        }
    } finally {
        pageLoading.value = false
    }
}

onMounted(async () => {
    await loadPage()

    if (!STRIPE_PUBLISHABLE_KEY) {
        console.warn('STRIPE_PUBLISHABLE_KEY is not set. Stripe payments will not work.')
        return
    }

    try {
        stripe.value = await loadStripe(STRIPE_PUBLISHABLE_KEY)
        if (stripe.value) {
            elements.value = stripe.value.elements()
        }
    } catch (err) {
        console.error('Failed to initialize Stripe:', err)
        error.value = 'Payment system initialization failed'
    }
})


watch(showPayment, async (newVal) => {
    if (!newVal) {
        if (cardElement.value) {
            cardElement.value.unmount()
            cardElement.value = null
        }
        if (paymentRequestButton.value) {
            paymentRequestButton.value.unmount()
            paymentRequestButton.value = null
        }
    }
})

const handleDonate = async () => {
    if (!donationForm.value.amount || donationForm.value.amount <= 0) {
        error.value = 'Please enter a valid donation amount'
        return
    }

    error.value = null
    showPaymentMethod.value = true
}

const selectPaymentMethod = (method) => {
    if (paypalCheckoutUrl.value && method !== 'paypal') {
        cancelPayPalRedirect()
    }
    if (stripeCheckoutUrl.value && method !== 'stripe') {
        cancelStripeRedirect()
    }
    selectedPaymentMethod.value = method
    error.value = null
}

const cancelPayPalRedirect = () => {
    paypalCheckoutUrl.value = null
    processing.value = false
    selectedPaymentMethod.value = null
}

const cancelStripeRedirect = () => {
    stripeCheckoutUrl.value = null
    processing.value = false
    selectedPaymentMethod.value = null
}

const cancelPayment = () => {
    if (cardElement.value) {
        cardElement.value.unmount()
        cardElement.value = null
    }
    if (paymentRequestButton.value) {
        paymentRequestButton.value.unmount()
        paymentRequestButton.value = null
    }
    paymentIntentId.value = null
    clientSecret.value = null
    showPayment.value = false
    showPaymentMethod.value = true
    selectedPaymentMethod.value = null
    processing.value = false
    error.value = null
}

const proceedWithPaymentMethod = async () => {
    if (!selectedPaymentMethod.value) {
        error.value = 'Please select a payment method'
        return
    }

    processing.value = true
    error.value = null

    try {
        if (selectedPaymentMethod.value === 'paypal') {
            const response = await donationService.createDonationCheckout(
                donationForm.value.amount,
                'paypal',
                donationForm.value.name || null,
                donationForm.value.email || null,
                donationForm.value.note || null
            )

            if (!response || !response.checkout_url) {
                throw new Error('Invalid response from PayPal. Please try again.')
            }

            paypalCheckoutUrl.value = response.checkout_url
            processing.value = false

            setTimeout(() => {
                if (paypalCheckoutUrl.value) {
                    window.location.href = paypalCheckoutUrl.value
                }
            }, 500)
        } else if (selectedPaymentMethod.value === 'stripe') {
            paypalCheckoutUrl.value = null
            const response = await donationService.createDonationCheckout(
                donationForm.value.amount,
                'stripe',
                donationForm.value.name || null,
                donationForm.value.email || null,
                donationForm.value.note || null
            )

            if (!response || !response.checkout_url) {
                throw new Error('Invalid response from Stripe. Please try again.')
            }

            stripeCheckoutUrl.value = response.checkout_url
            processing.value = false

            setTimeout(() => {
                if (stripeCheckoutUrl.value) {
                    window.location.href = stripeCheckoutUrl.value
                }
            }, 500)
        }
    } catch (err) {
        error.value = err.response?.data?.error || err.message || 'Failed to initialize payment'
        console.error('Payment initialization error:', err)
        processing.value = false
    }
}

const handlePayment = async () => {
    if (!stripe.value || !cardElement.value || !clientSecret.value) {
        error.value = 'Payment system not ready'
        return
    }

    processing.value = true
    error.value = null

    try {
        const { error: stripeError, paymentIntent } = await stripe.value.confirmCardPayment(
            clientSecret.value,
            {
                payment_method: {
                    card: cardElement.value,
                    billing_details: {
                        name: donationForm.value.name || undefined,
                        email: donationForm.value.email || undefined
                    }
                }
            }
        )

        if (stripeError) {
            error.value = stripeError.message
            processing.value = false
        } else if (paymentIntent && paymentIntent.status === 'succeeded') {
            window.location.href = '/donate-success'
        }
    } catch (err) {
        error.value = 'Payment failed. Please try again.'
        console.error('Payment error:', err)
        processing.value = false
    }
}

onUnmounted(() => {
    if (cardElement.value) {
        cardElement.value.unmount()
    }
    if (paymentRequestButton.value) {
        paymentRequestButton.value.unmount()
    }
})
</script>

<style scoped>
#card-element {
    min-height: 40px;
}

:deep(a) {
    color: #2563eb;
    text-decoration: underline;
    transition: color 0.2s;
    font-family: inherit;
}

:deep(a):hover {
    color: #1e40af;
}

#payment-request-button :deep(button[aria-label*="Apple"]),
#payment-request-button :deep(button[aria-label*="apple"]),
#payment-request-button :deep(button[id*="apple"]),
#payment-request-button :deep([data-testid*="apple"]) {
    display: none !important;
}
</style>
